// A product's pages, worked out from its own rows (data.js) and the app's clock. Pure: no network,
// no React, nothing but format.js and timestamp.js, so a test can import it as it is.
//
// The menu (BUILD-PLAN stage 4): every product's core items in the same order, Home, Sessions,
// Recordings, Resources, then its type's extras, leaving out what the product doesn't have:
// - the masterclass is Home only (decision 32), with Resources once something is added;
// - Sessions only for a member who has live sessions to see, so The Funding Engine's buyers with no
//   intake don't get it (the critic's settlement, 3 Oct);
// - Resources only once something is placed in the product for them;
// - a paid resource pack is Home alone, its home being its Resources (the product-types plan);
// - the 1-2-1 draws its board's menu: Home, Sessions, Recordings, Resources, Actions, and so does any
//   other private client space, whose Actions come with its type when its settings name none;
// - a mastermind (the Accelerator) adds Actions and Group. Accelerator.dc.html's 7th item,
//   Introductions, is left out (Adam, 5 Oct: introductions carry on outside the hub). Its Actions,
//   which members use every week, come straight after Home: Home, Actions, Sessions, Recordings,
//   Resources, Group (the Accelerator critique's member 14, 20 Oct; a mastermind's menu only).
// Each item is a link to the product's own page for it: /funding-engine/resources.
import * as f from '../lib/format.js'

export const MENU_LABELS = {
  home: 'Home', sessions: 'Sessions', recordings: 'Recordings', resources: 'Resources',
  actions: 'Actions', group: 'Group',
  'start-here': 'Start here', episodes: 'Pick your patch',
}
export const CORE_PAGES = ['home', 'sessions', 'recordings', 'resources']
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// A product's type extras, in order: catalogue.settings.menu_extras when it's set (the 1-2-1's
// ['actions']), else the type's own (a mastermind's Actions and Group; a private client space's
// Actions; Property Cashflow For Life's Start here and Pick your patch, when it has steps for them).
// Never Introductions (Adam, 5 Oct).
export const MASTERMIND_EXTRAS = ['actions', 'group']
export const CLIENT_SPACE_EXTRAS = ['actions']
export function extrasFor(product, data) {
  const set = product?.settings?.menu_extras
  if (Array.isArray(set)) return set.filter(k => typeof k === 'string' && /^[a-z0-9-]+$/.test(k) && k !== 'introductions')
  if (product?.type === 'mastermind') return MASTERMIND_EXTRAS
  if (product?.type === 'client_space') return CLIENT_SPACE_EXTRAS
  if (product?.type === 'on_demand') {
    const steps = data?.steps || []
    return [
      ...(steps.some(s => s.section === 'start_here') ? ['start-here'] : []),
      ...(steps.some(s => s.section === 'episodes') ? ['episodes'] : []),
    ]
  }
  return []
}

// The menu: [{ key, label, href }]
export function menuFor(product, data) {
  if (!product) return []
  const base = product.path || '/'
  const has = {
    sessions: (data?.sessions || []).length > 0,
    recordings: (data?.recordings || []).length > 0,
    resources: (data?.placements || []).length > 0,
  }
  let keys
  switch (product.type) {
    case 'masterclass': keys = ['home', ...(has.resources ? ['resources'] : [])]; break
    case 'client_space': keys = ['home', 'sessions', 'recordings', 'resources']; break
    case 'resource_pack': keys = ['home']; break
    case 'on_demand': keys = ['home', ...(has.recordings ? ['recordings'] : []), ...(has.resources ? ['resources'] : [])]; break
    default: keys = CORE_PAGES.filter(k => k === 'home' || has[k])
  }
  keys = [...keys, ...extrasFor(product, data).filter(k => !keys.includes(k))]
  // a mastermind's Actions straight after Home (member 14)
  if (product.type === 'mastermind' && keys.includes('actions')) keys = ['home', 'actions', ...keys.filter(k => k !== 'home' && k !== 'actions')]
  return keys.map(key => ({ key, label: menuLabel(key), href: key === 'home' ? base : `${base}/${key}` }))
}

// 'start-here' -> 'Start here' for an extra with no label of its own
export function menuLabel(key) {
  if (MENU_LABELS[key]) return MENU_LABELS[key]
  const words = key.replace(/-/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// Which page a path inside a product is:
// - home: the product's own path
// - sessions, recordings, resources, certificate, or a type extra (actions, group, start-here, ...)
// - tool: /<product>/tools/<placement id>, an online tool open in the tool runner (ToolPage.jsx), with
//   the placement's id in route.placementId
// - start-here: /<product>/start-here, and up to 2 more segments in route.rest (POYT's
//   /poyt/start-here/<row key>/<screen key>), for a product with Start here steps
// - week: a module's key (/funding-engine/week-2) or a recording's own key (/cashflow/m3a); the
//   recording page, with the module and the recording to play
// - other: anything else, left to the product's own screen (or "nothing here")
export function routeFor(path, product, data) {
  const base = product?.path || ''
  const rest = path.startsWith(base) ? path.slice(base.length) : path
  const segments = rest.split('/').filter(Boolean)
  if (!segments.length) return { page: 'home', segments }
  const [first] = segments
  if (first === 'tools' && segments.length === 2 && UUID.test(segments[1])) {
    return { page: 'tool', segments, placementId: segments[1].toLowerCase() }
  }
  // Start here and what's under it (/poyt/start-here/<row or screen key>), for a product with Start
  // here steps, whether or not its menu has the item (POYT.dc.html opens it from the home's 2 rows)
  if (first === 'start-here' && segments.length <= 3 && (data?.steps || []).some(st => st.section === 'start_here')) {
    return { page: 'start-here', segments, rest: segments.slice(1) }
  }
  if (segments.length === 1) {
    if (CORE_PAGES.includes(first) || first === 'certificate') return { page: first, segments }
    // a mastermind's accountability log: a page of its own that isn't in the menu, under Actions
    if (first === 'accountability-log' && product?.type === 'mastermind') return { page: first, segments }
    if (extrasFor(product, data).includes(first)) return { page: first, segments }
    const mod = (data?.modules || []).find(m => m.key === first)
    if (mod) return { page: 'week', segments, module: mod, recording: recordingForModule(data, mod.id) }
    const rec = (data?.recordings || []).find(r => r.key === first)
    if (rec) {
      const recMod = rec.module_id ? (data?.modules || []).find(m => m.id === rec.module_id) || null : null
      return { page: 'week', segments, module: recMod, recording: rec }
    }
  }
  return { page: 'other', segments }
}

// Where an online tool opens: its page in the hub, in the tool runner (ToolPage.jsx). The resource's
// Open button and a home's tool card (POYT.dc.html's "Open the analyser") link here, in a new tab.
export function toolHref(product, placementId) {
  return `${product?.path || ''}/tools/${placementId}`
}

// The menu item a page sits under. A week sits under Sessions where the member has them, as
// FEWeek.dc.html draws it, and under Recordings where they don't; a recording that isn't a week's or
// a module's (a mastermind's group session) sits under Recordings. The certificate is Home's, and a
// tool sits under Resources, where it opened from. route: routeFor's answer, for the recording page.
export function activeFor(page, menu, route = null) {
  const keys = menu.map(m => m.key)
  if (page === 'tool') return keys.includes('resources') ? 'resources' : 'home'
  if (page === 'week' && route?.recording && !route.module && keys.includes('recordings')) return 'recordings'
  if (page === 'week') return keys.includes('sessions') ? 'sessions' : keys.includes('recordings') ? 'recordings' : 'home'
  if (page === 'certificate' || page === 'other') return 'home'
  if (page === 'accountability-log') return keys.includes('actions') ? 'actions' : 'home'
  return keys.includes(page) ? page : 'home'
}

// ---- Weeks, modules and recordings

// The recordings of a module, in their order
export function moduleRecordings(data, moduleId) {
  return (data?.recordings || []).filter(r => r.module_id === moduleId).sort((a, b) => a.sort_order - b.sort_order)
}

// The recording a module's page plays first: the first released one, else the first
export function recordingForModule(data, moduleId) {
  const recs = moduleRecordings(data, moduleId)
  return recs.find(r => isReleased(data, r.id)) || recs[0] || null
}

// A recording is released to them when its content comes back (recording_content is readable only
// then: switched on, and past its session for their intake)
export function isReleased(data, recordingId) {
  return (data?.content || []).some(c => c.recording_id === recordingId)
}

// The numbered modules, in order: Week 1 to Week 6, Module 1 to Module 5
export function numberedModules(data) {
  return (data?.modules || []).filter(m => m.is_published && m.number != null).sort((a, b) => a.sort_order - b.sort_order)
}

// Their own intake's session for a module (sessions come back only for the intake they belong to)
export function sessionForModule(data, moduleId) {
  return (data?.sessions || []).filter(s => s.module_id === moduleId && s.status !== 'cancelled' && s.starts_at)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0] || null
}

// "Week 2 of 6, Wednesday 16 September" (phone: "Week 2 of 6, Wed 16 Sep"). The date is their own
// intake's session for the week; with no intake there's no date.
export function weekEyebrow(data, module, { phone = false } = {}) {
  if (!module) return ''
  const all = numberedModules(data)
  const of = module.number != null && all.length ? `${module.label} of ${all.length}` : module.label
  const s = sessionForModule(data, module.id)
  if (!s) return of
  return `${of}, ${phone ? f.shortDate(s.starts_at) : f.longDate(s.starts_at)}`
}

// The recording's own date: "Recorded live, 16 September"
export function recordedLine(recording) {
  if (!recording?.recorded_on) return ''
  return `Recorded live, ${f.dayMonthLong(f.calendarDay(recording.recorded_on))}`
}

// The recorded line on a product's recordings, unless the product's settings switch it off
// (catalogue.settings.recorded_line false: The Funding Engine, whose Weeks 3 to 6 are cohort 2's, so
// their dates would sit before Weeks 1 and 2's; Adam, 3 Oct)
export function recordedLineFor(product, recording) {
  if (product?.settings?.recorded_line === false) return ''
  return recordedLine(recording)
}

// A product whose access has ended, on its Resources page (Adam, 3 Oct; no board draws it): "Your
// access to The Funding Engine has ended. What's here is yours for life.", and the line for the rare
// visit with nothing left
const productWords = product => product?.name || ''
export const forLifeIntro = product => `Your access to ${productWords(product)} has ended. What's here is yours for life.`
export const forLifeEmpty = product => `Nothing from ${productWords(product)} is yours for life.`

// The resource items open to them in this product: what a held sentence in a recording's words waits
// for (data.open is the database's own answer for each placement)
export function openItemIds(data) {
  return new Set((data?.placements || []).filter(pl => !pl.removed_at && data.open?.[pl.id] === true).map(pl => pl.item_id))
}

// A key learning's body with its held sentences put back where they belong, each only while the
// resource item it promises is open to them (recording_content.key_learnings[].held: [{ text, after,
// item_id }], docs/data-model.md 10.2). The body itself never carries a held sentence, so anything
// that draws the body without this shows no promise of a file that isn't there.
export function learningBody(learning, openItems) {
  let body = String(learning?.body || '')
  for (const h of Array.isArray(learning?.held) ? learning.held : []) {
    if (!h || typeof h.text !== 'string' || !h.text || !openItems?.has(h.item_id)) continue
    const at = h.after ? body.indexOf(h.after) : -1
    if (at < 0) continue
    const end = at + h.after.length
    body = `${body.slice(0, end)} ${h.text}${body.slice(end)}`
  }
  return body
}

// 'Week 2' -> 'W2', 'Module 3' -> 'M3', 'Introduction' -> 'Intro'
export function chipLabel(module) {
  const m = /^(\w)\w*\s+(\d+)$/.exec(module.label || '')
  if (m) return `${m[1].toUpperCase()}${m[2]}`
  return (module.label || '').length > 6 ? module.label.slice(0, 5) : module.label
}

// The previous and next module, for the cards under a week
export function neighbours(data, module) {
  const list = (data?.modules || []).filter(m => m.is_published).sort((a, b) => a.sort_order - b.sort_order)
  const i = list.findIndex(m => m.id === module?.id)
  if (i < 0) return { prev: null, next: null }
  return { prev: list[i - 1] || null, next: list[i + 1] || null }
}

// "Week 1: Positioning, your profile and the rules", or the title alone when it is the label
export function moduleName(m) {
  if (!m) return ''
  return m.label === m.title || f.isPlaceholder(m.title) ? m.label : `${m.label}: ${m.title}`
}

// Homework for a module (or 1 recording), in order; kind 'homework' counts, 'commitment' doesn't
export function homeworkFor(data, moduleId) {
  return (data?.homework || []).filter(h => h.module_id === moduleId).sort((a, b) => a.sort_order - b.sort_order)
}

// "4 of 6 done", or null when nothing here counts (Week 6's commitments only)
export function homeworkCount(items, ticks) {
  const counted = items.filter(h => h.kind !== 'commitment')
  if (!counted.length) return null
  const done = counted.filter(h => ticks.includes(h.id)).length
  return `${done} of ${counted.length} done`
}

// What a report from the player saves: the position this moment, and the furthest point and the
// seconds watched only ever growing, on top of what was saved before this visit (base), each kept at
// or under the recording's length (the database caps them too)
export function progressPayload(base, { position, furthest, watched }, duration) {
  const cap = n => Math.max(0, Math.floor(duration ? Math.min(duration, n) : n))
  return {
    position: cap(position),
    furthest: cap(Math.max(base.furthest || 0, furthest || 0)),
    watched: cap((base.watched || 0) + (watched || 0)),
  }
}

// Their place in a recording, or null
export function progressFor(data, recordingId) {
  return (data?.progress || []).find(p => p.recording_id === recordingId) || null
}

// The live sessions they can see that haven't ended, soonest first
export function upcomingSessions(data, nowMs) {
  return (data?.sessions || []).filter(s => s.status !== 'cancelled' && s.starts_at
    && Date.parse(s.starts_at) + s.duration_minutes * 60e3 > nowMs)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))
}

// Adam's announcement for this product (the layouts' Announcements section: "Shows only while you
// have 1 posted for this product"), from the Library's rows, or null. Hidden ones stay hidden.
export function productAnnouncement(lib, productId, nowMs) {
  if (!lib) return null
  const hidden = new Set((lib.hides || []).map(h => h.announcement_id))
  const a = (lib.announcements || [])
    .filter(x => x.product_id === productId && x.audience !== 'everyone' && !x.taken_down_at
      && Date.parse(x.starts_at) <= nowMs && (!x.comes_down_at || Date.parse(x.comes_down_at) > nowMs) && !hidden.has(x.id))
    .sort((x, y) => Date.parse(y.starts_at) - Date.parse(x.starts_at))[0]
  if (!a) return null
  return {
    id: a.id, from: `From Adam, ${f.shortDate(a.starts_at)}`, headline: a.headline, line: a.body_line,
    button: a.button_label && a.button_path ? { label: a.button_label, href: a.button_path } : null,
  }
}

// A group's or an intake's name with its schedule line after it, over a page's heading: "Group 2,
// monthly, Sundays" (Adam's answer 11 of 7 Oct). After the name, the schedule's first word goes lower
// case only when it's 1 of the plain words a schedule line starts with ("Monthly", "Every", "Twice"),
// so a day, a month, an initialism or a name Adam types first ("Wednesdays at 7pm", "Zoom, monthly",
// "Leeds, monthly") keeps its capital; standing alone (no name), the schedule keeps its capital, as
// admin shows it.
const LEAD_WORDS = new Set([
  'monthly', 'weekly', 'fortnightly', 'daily', 'quarterly', 'yearly', 'annually',
  'every', 'each', 'once', 'twice', 'alternate', 'first', 'second', 'third', 'fourth', 'last',
  'mornings', 'afternoons', 'evenings', 'weekdays', 'weekends', 'weeknights',
])
export function nameWithSchedule(name, schedule) {
  const line = typeof schedule === 'string' ? schedule.trim() : ''
  if (!name) return line || null
  if (!line) return name
  const first = (line.match(/^[A-Z][a-z]+/) || [''])[0]
  const lower = first && LEAD_WORDS.has(first.toLowerCase()) ? line[0].toLowerCase() + line.slice(1) : line
  return `${name}, ${lower}`
}

// Their intake: the cohort row that comes back for them (my_cohort_ids), the latest if there are 2
export function myIntake(data) {
  const list = (data?.cohorts || []).slice().sort((a, b) => String(b.starts_on || '').localeCompare(String(a.starts_on || '')))
  return list[0] || null
}
